import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, Crown, Sparkles, BookOpen, Heart, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export function FollowButton({ author, className = "" }) {
  const { user } = useApp();
  const nav = useNavigate();
  const [following, setFollowing] = useState(!!author.following);
  const [count, setCount] = useState(author.followers || 0);
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/authors/${author.id}/follow`);
      setFollowing(data.following); setCount(data.followers);
      toast.success(data.following ? `Following ${author.name}` : `Unfollowed ${author.name}`);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <span className="inline-flex items-center gap-2">
      <button onClick={toggle} disabled={busy} data-testid={`follow-${author.slug || author.id}`}
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${following ? "border border-primary text-primary" : "bg-primary text-primary-foreground hover:opacity-90"} ${className}`}>
        {following ? <UserCheck className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
        {following ? "Following" : "Follow"}
      </button>
      <span className="text-xs text-muted-foreground" data-testid={`followers-${author.slug || author.id}`}>{count.toLocaleString()} followers</span>
    </span>
  );
}

export function SectionHeader({ icon: Icon, title, action, to }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-2xl md:text-3xl font-display font-bold flex items-center gap-2.5">
        {Icon && <Icon className="h-6 w-6 text-primary" />} {title}
      </h2>
      {to && (
        <Link to={to} className="text-xs track-label text-primary hover:opacity-80 flex items-center gap-1">
          {action || "View All"} <ChevronRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon = BookOpen, title, subtitle }) {
  return (
    <div className="text-center py-20 px-6 border border-dashed border-border rounded-3xl surface-2" data-testid="empty-state">
      <Icon className="h-12 w-12 text-primary/50 mx-auto mb-4" />
      <h3 className="text-xl font-display font-semibold">{title}</h3>
      {subtitle && <p className="text-muted-foreground mt-2 max-w-md mx-auto">{subtitle}</p>}
    </div>
  );
}

export function BookCard({ book, className = "" }) {
  return (
    <Link to={`/books/${book.slug}`} data-testid={`book-card-${book.slug}`}
      className={`group block card-hover ${className}`}>
      <div className="relative aspect-[2/3] rounded-2xl overflow-hidden border border-border bg-secondary">
        {book.cover ? (
          <img src={book.cover} alt={book.title} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="h-full w-full grid place-items-center text-muted-foreground"><BookOpen className="h-10 w-10" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
          {book.new_release && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-primary text-primary-foreground flex items-center gap-1"><Sparkles className="h-3 w-3" />New</span>}
          {book.trending && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-black/70 text-white backdrop-blur">Trending</span>}
        </div>
        {book.premium && <span className="absolute top-2.5 right-2.5 h-7 w-7 grid place-items-center rounded-full bg-amber-400/90 text-black"><Crown className="h-3.5 w-3.5" /></span>}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="font-display font-semibold text-white leading-tight line-clamp-2">{book.title}</h3>
          {book.author && <p className="text-[11px] uppercase tracking-wide text-white/70 mt-1">{book.author.name}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2 px-0.5 text-xs text-muted-foreground">
        {book.genres?.[0] && <span>{book.genres[0].name}</span>}
        {book.genres?.[1] && <><span>·</span><span>{book.genres[1].name}</span></>}
        {book.rating_count > 0 && <span className="ml-auto flex items-center gap-1 text-amber-400"><Star className="h-3 w-3 fill-current" />{Number(book.rating).toFixed(1)}</span>}
      </div>
    </Link>
  );
}

export function Carousel({ children, testid }) {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 600, behavior: "smooth" });
  return (
    <div className="relative group/car">
      <div ref={ref} data-testid={testid} className="flex gap-4 overflow-x-auto no-scrollbar book-scroll pb-2 snap-x">
        {children}
      </div>
      <button onClick={() => scroll(-1)} className="absolute -left-3 top-1/3 h-9 w-9 rounded-full glass border border-border grid place-items-center opacity-0 group-hover/car:opacity-100 transition-opacity hidden md:grid">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button onClick={() => scroll(1)} className="absolute -right-3 top-1/3 h-9 w-9 rounded-full glass border border-border grid place-items-center opacity-0 group-hover/car:opacity-100 transition-opacity hidden md:grid">
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}

export function GenreCard({ genre }) {
  return (
    <Link to={`/genres/${genre.slug}`} data-testid={`genre-card-${genre.slug}`}
      className="group relative block aspect-[4/5] rounded-2xl overflow-hidden border border-border card-hover">
      {genre.image && <img src={genre.image} alt={genre.name} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-black/40 to-black/20 group-hover:from-primary/80 transition-colors" />
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="font-display font-bold text-white text-lg leading-tight">{genre.name}</h3>
        {genre.book_count != null && <p className="text-[11px] text-white/80 mt-0.5">{genre.book_count} stories</p>}
      </div>
    </Link>
  );
}

export function TropeCard({ trope }) {
  return (
    <Link to={`/tropes/${trope.slug}`} data-testid={`trope-card-${trope.slug}`}
      className="group relative block aspect-[3/2] rounded-2xl overflow-hidden border border-border card-hover">
      {trope.image && <img src={trope.image} alt={trope.name} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <span className="absolute top-2.5 right-2.5 h-8 w-8 grid place-items-center rounded-full bg-primary/90 text-white"><Heart className="h-4 w-4 fill-current" /></span>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <h3 className="font-display font-semibold text-white leading-tight">{trope.name}</h3>
      </div>
    </Link>
  );
}

export function AuthorCard({ author }) {
  return (
    <div data-testid={`author-card-${author.slug}`}
      className="group text-center p-6 rounded-2xl border border-border surface-2 card-hover">
      <Link to={`/authors/${author.slug}`}>
        <div className="h-24 w-24 mx-auto rounded-full overflow-hidden border-2 border-primary/40 bg-secondary">
          {author.avatar ? <img src={author.avatar} alt={author.name} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center text-2xl font-display">{author.name[0]}</div>}
        </div>
        <h3 className="font-display font-semibold text-lg mt-4">{author.name}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{author.bio}</p>
      </Link>
      <div className="mt-4 flex flex-col items-center gap-2">
        <FollowButton author={author} />
        <Link to={`/authors/${author.slug}`} className="text-xs text-primary">{author.book_count || 0} books →</Link>
      </div>
    </div>
  );
}
